import { AlertTriangle, Check, Copy } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type CopyStatus = "idle" | "copied" | "error";

type CopyContactButtonProps = {
  value: string;
  label: string;
  testId: string;
};

function copyWithSelection(value: string) {
  const previouslyFocused = document.activeElement as HTMLElement | null;
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "-9999px";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();

  try {
    if (!document.execCommand("copy")) {
      throw new Error("copy command was rejected");
    }
  } finally {
    textarea.remove();
    previouslyFocused?.focus({ preventScroll: true });
  }
}

async function copyText(value: string) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }
  } catch {
    // Some mobile browsers expose the API but reject it outside a secure context.
  }

  copyWithSelection(value);
}

export function CopyContactButton({ value, label, testId }: CopyContactButtonProps) {
  const [status, setStatus] = useState<CopyStatus>("idle");
  const resetTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);
    };
  }, []);

  const handleCopy = async () => {
    if (resetTimer.current !== null) window.clearTimeout(resetTimer.current);

    try {
      await copyText(value);
      setStatus("copied");
    } catch {
      setStatus("error");
    }

    resetTimer.current = window.setTimeout(() => setStatus("idle"), 1600);
  };

  const statusText =
    status === "copied" ? "已复制" : status === "error" ? "复制失败，请重试" : "";

  return (
    <span className="copy-contact-control">
      <button
        type="button"
        className="copy-contact-button"
        data-testid={testId}
        data-status={status}
        aria-label={`复制${label}`}
        title={status === "copied" ? `${label}已复制` : status === "error" ? `复制失败，重试复制${label}` : `复制${label}`}
        onClick={handleCopy}
      >
        {status === "copied" ? (
          <Check aria-hidden="true" />
        ) : status === "error" ? (
          <AlertTriangle aria-hidden="true" />
        ) : (
          <Copy aria-hidden="true" />
        )}
        <span>{status === "copied" ? "已复制" : status === "error" ? "重试" : "复制"}</span>
      </button>
      <span
        className={`copy-contact-status copy-contact-status--${status}`}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {statusText}
      </span>
    </span>
  );
}
